import type { NavigateFunction } from '@remix-run/react';
import type { TNavbarDropdownElementItem, TNavbarItemElement } from '@shared/dream-components';

import type { Subscription } from '~/interfaces/subscriptions';
import logout from '~/utils/logout';

export const getNodeActionAndVisibility = (
  node: TNavbarItemElement | TNavbarDropdownElementItem,
  subscription: Subscription | undefined,
  navigate: NavigateFunction,
  isLoggedIn: boolean,
  isPremiumUser: boolean,
) => {
  const onClick = node?.attrs && 'onClick' in node.attrs ? node.attrs.onClick : undefined;
  const action = node?.attrs && 'action' in node.attrs && node.attrs.action;
  const currentHref = node.attrs?.href || '';
  const isOpenAction = onClick === 'action' || (onClick === undefined && !!action);
  let url: URL | undefined;
  let handleClientSideNavigation: any;
  const navigateOptions: any = {};
  const showWhen = node.attrs?.showWhen;
  const isCustomLink = node.attrs?.isCustomLink;

  try {
    url = new URL(currentHref);
    // grab all the query params and hash of current window location
    // if it's internal linking, we want to keep app state.
    if (typeof window !== 'undefined' && !isCustomLink) {
      const currentWindow = new URL(window.location.href);
      url.search = currentWindow.search;
      url.hash = currentWindow.hash;
    }
  } catch {
    // Fail silently
  }

  const handleLogout = () => {
    logout('token');
  };

  // set up visibility based on showWhen
  if (node.attrs) {
    if (showWhen) {
      if (showWhen === 'all') {
        node.attrs.visible = true;
      } else if (showWhen === 'all_logged_out') {
        node.attrs.visible = !isLoggedIn;
      } else if (showWhen === 'all_logged_in') {
        node.attrs.visible = isLoggedIn;
      } else if (showWhen === 'paid') {
        node.attrs.visible = isPremiumUser && isLoggedIn;
      } else if (showWhen === 'free') {
        node.attrs.visible = !isPremiumUser && isLoggedIn;
      }
    } else {
      // if showWhen is not set, default to based on actions
      node.attrs.visible = true;

      if (
        node.attrs.action === 'login' ||
        (node.attrs.action as string) === 'sign_in' || // `sign_in` is WRONG, the corrent one is `login`, this is to support old data
        node.attrs.action === 'sign_up'
      ) {
        node.attrs.visible = !isLoggedIn;
      }

      if (node.attrs.action === 'profile') {
        node.attrs.visible = isLoggedIn;
      }

      if (node.attrs.action === 'logout') {
        node.attrs.visible = isLoggedIn;
      }
    }
  }

  // On actions we only want to
  if (isOpenAction) {
    if (typeof window !== 'undefined') {
      url = new URL(window.location.href);
    }
    if (action === 'login' && url) {
      url.searchParams.set('modal', 'login');
      navigateOptions.preventScrollReset = true;
    } else if (action === 'sign_up' && url) {
      url.searchParams.set('modal', 'signup');
      navigateOptions.preventScrollReset = true;
    } else if (action === 'profile' && url) {
      url.pathname = `subscribe/${subscription?.subscriber_id || ''}/manage`;
    } else if (action === 'search' && url) {
      url.searchParams.set('modal', 'search');
      navigateOptions.preventScrollReset = true;
    } else if (action === 'logout') {
      url = undefined;
      handleClientSideNavigation = handleLogout;
    }
  } else if (!url && !isCustomLink) {
    // we log error here because this is an invalid state
    //console.error('ERROR: no url for node.attrs:', node.attrs);
  }

  // Ensure the button is not open in a new tab if it has an action
  if (node.attrs && isOpenAction && url && 'new_tab' in node.attrs) {
    node.attrs.new_tab = false;
  }

  if (!(node.attrs && 'new_tab' in node.attrs && node.attrs?.new_tab) && url && !isCustomLink) {
    handleClientSideNavigation = () => {
      navigate(url.pathname + url.search + url.hash, navigateOptions);
    };
  }

  if (
    node.attrs &&
    'type' in node.attrs &&
    node.attrs?.type === 'user_button' &&
    node.attrs?.action === 'profile' &&
    node.attrs?.style === 'dropdown'
  ) {
    handleClientSideNavigation = undefined;
  }

  return {
    handleClientSideNavigation,
  };
};
